import React, { useState } from 'react'
import type { ActivePage } from './MainMenu'
import type { BottomNavKey } from './BottomNav'
import { Lock, Plus, LogIn } from 'lucide-react'
import { useAccount } from 'wagmi'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { CreatePrivateGameModal } from './modals/CreatePrivateGameModal'
import { JoinPrivateGameModal } from './modals/JoinPrivateGameModal'
import { playSoundSafe } from '../utils/audio'

interface PrivateRoomProps {
  onNavigateToPage?: (page: ActivePage) => void
  onNavClick: (nav: BottomNavKey) => void
  activeNav: BottomNavKey
  onOpenSuggestion: () => void
  onOpenSettings: () => void
  onOpenInventory: () => void
}

// AGC: Private room component for creating and joining private matches
// AGC: Follows Profile.tsx and Games.tsx design patterns for Base App consistency
export const PrivateRoom: React.FC<PrivateRoomProps> = ({ 
  onNavigateToPage,
}) => {
  const { address, isConnected } = useAccount()
  const { canBattle: canPlayBattle } = usePlayerStatus(address)

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showJoinModal, setShowJoinModal] = useState(false)

  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  const actions = [
    {
      id: 'create' as const,
      icon: Plus,
      title: 'Create Game',
      subtitle: 'Host a private match',
      description: 'Get a game code to share with friends',
      color: 'from-green-500 to-emerald-500',
      borderColor: 'border-green-400/40',
      bgColor: 'bg-green-600/20',
      hoverColor: 'hover:bg-green-600/30',
      textColor: 'text-green-300',
      disabled: !isConnected || !canPlayBattle,
      onClick: () => setShowCreateModal(true),
    },
    {
      id: 'join' as const,
      icon: LogIn,
      title: 'Join Game',
      subtitle: 'Enter a private match',
      description: 'Use a game code to join',
      color: 'from-blue-500 to-cyan-500',
      borderColor: 'border-blue-400/40',
      bgColor: 'bg-blue-600/20',
      hoverColor: 'hover:bg-blue-600/30',
      textColor: 'text-blue-300',
      disabled: !isConnected || !canPlayBattle,
      onClick: () => setShowJoinModal(true),
    },
  ]

  return (
    <>
      <main
        className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
        style={sharedTopPaddingStyle}
      >
        {/* AGC: Hero Header (No Card) - Following Profile.tsx pattern */}
        <section className="pb-2 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">PRIVATE MATCH</p>
              <h1 className="text-2xl font-bold leading-tight text-white">Private Room</h1>
              <p className="text-[11px] text-slate-400">Create or join a private game</p>
            </div>
            <div className="text-[10px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10">
              <Lock className="w-4 h-4 text-slate-400" />
            </div>
          </div>
        </section>

        {/* AGC: Action Buttons - Compact grid layout */}
        <section className="flex flex-col gap-2">
          {actions.map((action) => {
            const Icon = action.icon
            return (
              <button
                key={action.id}
                onClick={() => {
                  if (!action.disabled) {
                    playSoundSafe('/sounds/click.mp3', 0.3)
                  }
                  action.onClick()
                }}
                disabled={action.disabled}
                className={`
                  w-full rounded-xl border ${action.borderColor} ${action.bgColor} px-4 py-4 text-left
                  ${action.disabled ? 'opacity-50 cursor-not-allowed' : `${action.hoverColor} transition-all hover:scale-[1.01]`}
                  shadow-[0_8px_20px_rgba(0,0,0,0.45)]
                `}
              >
                <div className="flex items-center gap-3">
                  {/* AGC: Icon with gradient background */}
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${action.color} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  
                  {/* AGC: Content */}
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${action.textColor} mb-1`}>{action.title}</p>
                    <p className="text-[11px] text-slate-300 mb-0.5">{action.subtitle}</p>
                    <p className="text-[10px] text-slate-500">{action.description}</p>
                  </div>

                  {/* AGC: Arrow indicator */}
                  {!action.disabled && (
                    <div className="text-slate-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </section>

        {/* AGC: Info Section - Compact design */}
        <section className="bg-slate-950/60 border border-slate-900 rounded-2xl p-2.5 shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-[11px]">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
              <span className="text-slate-300">Private matches use the same 50 OGB entry fee</span>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
              <span className="text-slate-300">Share game code with friends to play together</span>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
              <span className="text-slate-300">Password required to join private matches</span>
            </div>
          </div>
        </section>

        {/* AGC: Wallet Status Warning */}
        {(!isConnected || !canPlayBattle) && (
          <section className="bg-slate-950/60 border border-slate-900 rounded-2xl p-2.5 shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
            <div className="text-center py-2">
              <p className="text-[11px] text-slate-400 mb-1">
                {!isConnected ? 'Connect wallet to start battling' : 'Insufficient OGB balance (50 OGB required)'}
              </p>
            </div>
          </section>
        )}
      </main>

      {/* AGC: Create Private Game Modal */}
      {showCreateModal && (
        <CreatePrivateGameModal
          onClose={() => setShowCreateModal(false)}
          onGameCreated={(gameNumber, password) => {
            console.log(`[PrivateRoom] Game created: #${gameNumber} with password: ${password}`)
            // AGC: Navigate to P2P battle page with private game info
            setShowCreateModal(false)
            // AGC: Store private game info in sessionStorage for P2P battle to pick up
            sessionStorage.setItem('privateGame', JSON.stringify({ gameNumber, password, isHost: true }))
            // AGC: Navigate to P2P battle page
            onNavigateToPage && onNavigateToPage('p2pBattle')
          }}
        />
      )}

      {/* AGC: Join Private Game Modal */}
      {showJoinModal && (
        <JoinPrivateGameModal
          onClose={() => setShowJoinModal(false)}
          onGameJoined={(gameNumber, password) => {
            console.log(`[PrivateRoom] Joining game: #${gameNumber} with password: ${password}`)
            // AGC: Navigate to P2P battle page with private game info
            setShowJoinModal(false)
            // AGC: Store private game info in sessionStorage for P2P battle to pick up
            sessionStorage.setItem('privateGame', JSON.stringify({ gameNumber, password, isHost: false }))
            // AGC: Navigate to P2P battle page
            onNavigateToPage && onNavigateToPage('p2pBattle')
          }}
        />
      )}
    </>
  )
}

